import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Message,
  Modal,
  Select,
  Switch,
} from '@arco-design/web-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createModelMetaApi,
  getModelMetaApi,
  listVendorsApi,
  updateModelMetaApi,
  type VendorMeta,
} from '@/api/models'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import { modalWidth } from '@/utils/responsive'

const ENDPOINT_TEMPLATE = `{
  "openai": {"path": "/v1/chat/completions", "method": "POST"}
}`

export const NAME_RULE_OPTIONS = [
  { value: 0, label: '精确名称匹配' },
  { value: 1, label: '前缀名称匹配' },
  { value: 2, label: '包含名称匹配' },
  { value: 3, label: '后缀名称匹配' },
]

export interface ModelFormSeed {
  id?: number
  model_name?: string
  name_rule?: number
}

interface Props {
  visible: boolean
  seed?: ModelFormSeed | null
  onCancel: () => void
  onSuccess?: () => void
}

function parseTags(tags?: string): string[] {
  if (!tags) return []
  return tags.split(',').map((t) => t.trim()).filter(Boolean)
}

function validateEndpointsJson(value: string | undefined): string | null {
  if (!value || !value.trim()) return null
  try {
    const parsed = JSON.parse(value)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return '端点须为 JSON 对象'
    }
    return null
  } catch {
    return '端点 JSON 格式无效'
  }
}

export default function ModelFormModal({ visible, seed, onCancel, onSuccess }: Props) {
  const { isMobile } = useBreakpoint()
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [loading, setLoading] = useState(false)
  const isEdit = Boolean(seed?.id)

  const vendorsQuery = useQuery({
    queryKey: ['vendors-meta', 'form'],
    queryFn: async () => {
      const data = await listVendorsApi({ page_size: 1000 })
      return data.items || []
    },
    enabled: visible,
  })

  const vendorOptions = useMemo(
    () =>
      (vendorsQuery.data || []).map((v: VendorMeta) => ({
        value: v.id,
        label: v.name,
      })),
    [vendorsQuery.data]
  )

  useEffect(() => {
    if (!visible) {
      form.resetFields()
      return
    }

    const load = async () => {
      if (isEdit && seed?.id) {
        setLoading(true)
        try {
          const data = await getModelMetaApi(seed.id)
          form.setFieldsValue({
            model_name: data.model_name,
            name_rule: data.name_rule ?? 0,
            icon: data.icon || '',
            description: data.description || '',
            tags: parseTags(data.tags),
            vendor_id: data.vendor_id || undefined,
            endpoints: data.endpoints || '',
            sync_official: data.sync_official === 1,
            status: data.status === 1,
          })
        } catch (e) {
          Message.error(e instanceof Error ? e.message : '加载模型失败')
          onCancel()
        } finally {
          setLoading(false)
        }
        return
      }

      form.setFieldsValue({
        model_name: seed?.model_name || '',
        name_rule: seed?.name_rule ?? 0,
        icon: '',
        description: '',
        tags: [],
        vendor_id: undefined,
        endpoints: '',
        sync_official: true,
        status: true,
      })
    }

    void load()
  }, [visible, seed?.id, seed?.model_name, seed?.name_rule, isEdit, form, onCancel])

  const submit = async () => {
    try {
      const values = await form.validate()
      const endpointsErr = validateEndpointsJson(values.endpoints)
      if (endpointsErr) {
        Message.error(endpointsErr)
        return
      }

      const payload = {
        model_name: String(values.model_name).trim(),
        name_rule: Number(values.name_rule),
        description: values.description || '',
        icon: values.icon || '',
        tags: Array.isArray(values.tags) ? values.tags.join(',') : '',
        vendor_id: values.vendor_id ? Number(values.vendor_id) : 0,
        endpoints: values.endpoints || '',
        status: values.status ? 1 : 0,
        sync_official: values.sync_official ? 1 : 0,
      }

      setLoading(true)
      if (isEdit && seed?.id) {
        await updateModelMetaApi({ ...payload, id: seed.id })
        Message.success('模型已更新')
      } else {
        await createModelMetaApi(payload)
        Message.success('模型已创建')
      }
      await qc.invalidateQueries({ queryKey: ['models-meta'] })
      onSuccess?.()
      onCancel()
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={isEdit ? '编辑模型' : '添加模型'}
      visible={visible}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={loading}
      style={{ width: isMobile ? modalWidth(640) : 640 }}
      unmountOnExit
    >
      <Alert
        type="info"
        className="!mb-4"
        content="此处配置仅影响「可用模型」对用户的展示效果，不会影响模型的实际调用与路由。若需配置真实调用行为，请前往「渠道管理」。"
      />
      <Form form={form} layout="vertical" disabled={loading}>
        <Form.Item
          field="model_name"
          label="模型名称"
          rules={[{ required: true, message: '请输入模型名称' }]}
        >
          <Input placeholder="如 gpt-4o" allowClear />
        </Form.Item>
        <Form.Item
          field="name_rule"
          label="名称匹配类型"
          rules={[{ required: true, message: '请选择名称匹配类型' }]}
          extra="根据模型名称和匹配规则查找模型元数据，优先级：精确 > 前缀 > 后缀 > 包含"
        >
          <Select options={NAME_RULE_OPTIONS} />
        </Form.Item>
        <Form.Item field="icon" label="模型图标" extra="图标使用 @lobehub/icons 库，如 OpenAI、Claude.Color">
          <Input placeholder="图标名称" allowClear />
        </Form.Item>
        <Form.Item field="description" label="描述">
          <Input.TextArea placeholder="模型描述" rows={3} allowClear />
        </Form.Item>
        <Form.Item field="tags" label="标签">
          <Select
            mode="multiple"
            allowCreate
            placeholder="输入标签，回车添加"
            options={[]}
          />
        </Form.Item>
        <Form.Item field="vendor_id" label="供应商">
          <Select
            placeholder="选择供应商"
            options={vendorOptions}
            allowClear
            showSearch
            loading={vendorsQuery.isLoading}
          />
        </Form.Item>
        <Form.Item
          field="endpoints"
          label="展示端点（JSON）"
          extra="留空则使用默认端点；支持 {path, method} 结构"
        >
          <Input.TextArea
            placeholder={ENDPOINT_TEMPLATE}
            rows={5}
            allowClear
          />
        </Form.Item>
        <div className="mb-3">
          <Button
            type="text"
            size="small"
            onClick={() => form.setFieldValue('endpoints', ENDPOINT_TEMPLATE)}
          >
            填入端点模板
          </Button>
        </div>
        <Form.Item field="sync_official" label="参与官方同步" triggerPropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item field="status" label="启用" triggerPropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  )
}
